import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, FormControl, InputLabel, Select, MenuItem,
    Typography, Chip, CircularProgress, IconButton, useTheme, useMediaQuery,
} from '@mui/material';
import { Close, CloudUpload, AttachFile, Cancel as CancelIcon, Save } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import { trackAction } from 'utils/Infralytiqs';

interface Props {
    open: boolean;
    onClose: () => void;
    onCreated: () => void;
}

const PRIORITIES = [
    { value: 'P1', label: 'P1 : Production Blocker Issues' },
    { value: 'P2', label: 'P2 : Urgent, serious interruptions to normal operations' },
    { value: 'P3', label: 'P3 : Important, Interruptions to normal operations' },
    { value: 'P4', label: 'P4 : Minor, Minimal or no interruptions to normal operations' },
];

const ALL_PRODUCTS = [
    'InstantUpdate.ai',
    'CampaignKing.ai',
    'AssetManagement.ai',
    'Infralytiqs.com',
    'AutomateTest.ai',
];

const MODULE_TO_PRODUCT: Record<string, string> = {
    'instantupdate.ai': 'InstantUpdate.ai',
    'campaignking.ai': 'CampaignKing.ai',
    'assetsmanagement.ai': 'AssetManagement.ai',
    'infralytiqs.com': 'Infralytiqs.com',
    'automatetest.ai': 'AutomateTest.ai',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[1-9]\d{1,14}$/;
const MAX_FILES = 4;
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

const isFileTypeAllowed = (file: File): boolean => {
    if (file.type.startsWith('image/') || file.type.startsWith('video/')) return true;
    const ext = file.name.toLowerCase().split('.').pop();
    return ext === 'csv' || ext === 'xls' || ext === 'xlsx';
};

const INITIAL_FORM = {
    priority: '',
    title: '',
    description: '',
    productName: '',
    contactNumber: '',
    mobileNumber: '',
    emailAddress: '',
};

export const NewTicketDialog: React.FC<Props> = ({ open, onClose, onCreated }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const user = useSelector((state: any) => state.authentication?.user);

    const availableProducts = useMemo(() => {
        const modules: Array<{ module: string; role: string }> = user?.moduleAndRole || [];
        if (modules.length === 0) return ALL_PRODUCTS;
        const mapped = modules
            .map(m => MODULE_TO_PRODUCT[m.module?.toLowerCase()])
            .filter(Boolean);
        return mapped.length > 0 ? mapped : ALL_PRODUCTS;
    }, [user]);

    const [form, setForm] = useState(INITIAL_FORM);
    const [ccEmails, setCcEmails] = useState<string[]>([]);
    const [ccInput, setCcInput] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dropZoneRef = useRef<HTMLDivElement>(null);
    const [dragActive, setDragActive] = useState(false);

    const handleChange = (field: string) => (e: any) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleCcKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
            e.preventDefault();
            addCcEmail();
        }
    };

    const addCcEmail = () => {
        const email = ccInput.trim().replace(/,$/g, '');
        if (!email) return;
        if (!EMAIL_REGEX.test(email)) {
            setFieldErrors(prev => ({ ...prev, ccEmailAddresses: 'Invalid email format' }));
            return;
        }
        if (ccEmails.includes(email)) {
            setFieldErrors(prev => ({ ...prev, ccEmailAddresses: 'Email already added' }));
            return;
        }
        setCcEmails(prev => [...prev, email]);
        setCcInput('');
        setFieldErrors(prev => ({ ...prev, ccEmailAddresses: '' }));
    };

    const removeCcEmail = (email: string) => {
        setCcEmails(prev => prev.filter(e => e !== email));
    };

    const addFiles = useCallback((incoming: FileList | File[]) => {
        const newFiles = Array.from(incoming);
        const errors: string[] = [];

        const rejected = newFiles.filter(f => !isFileTypeAllowed(f));
        if (rejected.length > 0) {
            errors.push(`Unsupported file type: ${rejected.map(f => f.name).join(', ')}`);
        }

        const oversized = newFiles.filter(f => f.size > MAX_FILE_SIZE_BYTES);
        if (oversized.length > 0) {
            errors.push(`File(s) exceed 20 MB: ${oversized.map(f => f.name).join(', ')}`);
        }

        const valid = newFiles.filter(f => isFileTypeAllowed(f) && f.size <= MAX_FILE_SIZE_BYTES);

        setFiles(prev => {
            const remaining = MAX_FILES - prev.length;
            if (remaining <= 0) {
                errors.push(`Maximum ${MAX_FILES} files allowed`);
                if (errors.length) setFieldErrors(p => ({ ...p, files: errors.join('. ') }));
                return prev;
            }
            const toAdd = valid.slice(0, remaining);
            if (valid.length > remaining) {
                errors.push(`Only ${remaining} more file(s) can be added (max ${MAX_FILES})`);
            }
            if (errors.length) setFieldErrors(p => ({ ...p, files: errors.join('. ') }));
            else setFieldErrors(p => ({ ...p, files: '' }));
            return [...prev, ...toAdd];
        });
    }, []);

    const removeFile = useCallback((index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
        setFieldErrors(prev => ({ ...prev, files: '' }));
    }, []);

    const handleFileDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            addFiles(e.dataTransfer.files);
        }
    }, [addFiles]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragActive(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragActive(false);
    }, []);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            addFiles(e.target.files);
            e.target.value = '';
        }
    };

    const validate = (): boolean => {
        const errors: Record<string, string> = {};
        if (!form.priority) errors.priority = 'Priority is required';
        if (!form.title.trim()) errors.title = 'Title is required';
        if (!form.description.trim()) errors.description = 'Description is required';
        if (!form.productName) errors.productName = 'Product is required';
        if (!form.emailAddress.trim()) {
            errors.emailAddress = 'Email is required';
        } else if (!EMAIL_REGEX.test(form.emailAddress.trim())) {
            errors.emailAddress = 'Invalid email format';
        }
        if (form.contactNumber.trim() && !PHONE_REGEX.test(form.contactNumber.trim())) {
            errors.contactNumber = 'Enter a valid phone number with country code (e.g. +1234567890)';
        }
        if (form.mobileNumber.trim() && !PHONE_REGEX.test(form.mobileNumber.trim())) {
            errors.mobileNumber = 'Enter a valid phone number with country code (e.g. +1234567890)';
        }
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const fileToBase64 = (f: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(f);
        });
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        setError('');

        try {
            const fileAttachments = await Promise.all(
                files.map(async (f) => {
                    const base64Data = await fileToBase64(f);
                    return { data: base64Data, name: f.name, mimeType: f.type };
                })
            );

            const createResp: any = await apiClient.post('/support/ticket/new', {
                ...form,
                title: form.title.trim(),
                description: form.description.trim(),
                emailAddress: form.emailAddress.trim(),
                contactNumber: form.contactNumber.trim(),
                mobileNumber: form.mobileNumber.trim(),
                ccEmailAddresses: ccEmails,
                fileAttachments: fileAttachments.length > 0 ? fileAttachments : undefined,
            });

            trackAction('support_ticket_created', (form as any)?.priority, {
                ticket_id: createResp?.data?._id,
                priority: (form as any)?.priority,
                product: (form as any)?.product,
                cc_count: ccEmails.length,
                attachment_count: fileAttachments.length,
                user_email: form.emailAddress.trim(),
            });

            setForm(INITIAL_FORM);
            setCcEmails([]);
            setCcInput('');
            setFiles([]);
            setFieldErrors({});
            onCreated();
        } catch (err: any) {
            Logger.error('NewTicketDialog', `Failed to create ticket: ${err}`);
            setError(err?.message || 'Failed to create ticket. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (saving) return;
        setForm(INITIAL_FORM);
        setCcEmails([]);
        setCcInput('');
        setFiles([]);
        setFieldErrors({});
        setError('');
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="span" fontWeight={700}>New Support Ticket</Typography>
                <IconButton onClick={handleClose} disabled={saving}><Close /></IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
                {error && (
                    <Typography color="error" variant="body2" sx={{ p: 1, borderRadius: 1, bgcolor: 'error.light', color: 'error.contrastText' }}>
                        {error}
                    </Typography>
                )}

                <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                    <FormControl fullWidth error={!!fieldErrors.priority}>
                        <InputLabel color="secondary">Priority *</InputLabel>
                        <Select
                            value={form.priority}
                            label="Priority *"
                            onChange={handleChange('priority')}
                            color="secondary"
                        >
                            {PRIORITIES.map(p => (
                                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                            ))}
                        </Select>
                        {fieldErrors.priority && <Typography variant="caption" color="error">{fieldErrors.priority}</Typography>}
                    </FormControl>

                    <FormControl fullWidth error={!!fieldErrors.productName}>
                        <InputLabel color="secondary">Product *</InputLabel>
                        <Select
                            value={form.productName}
                            label="Product *"
                            onChange={handleChange('productName')}
                            color="secondary"
                        >
                            {availableProducts.map(p => (
                                <MenuItem key={p} value={p}>{p}</MenuItem>
                            ))}
                        </Select>
                        {fieldErrors.productName && <Typography variant="caption" color="error">{fieldErrors.productName}</Typography>}
                    </FormControl>
                </Box>

                <TextField
                    label="Title *"
                    value={form.title}
                    onChange={handleChange('title')}
                    fullWidth
                    color="secondary"
                    error={!!fieldErrors.title}
                    helperText={fieldErrors.title}
                />

                <TextField
                    label="Description *"
                    value={form.description}
                    onChange={handleChange('description')}
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={8}
                    color="secondary"
                    error={!!fieldErrors.description}
                    helperText={fieldErrors.description}
                />

                {/* File Drop Zone */}
                <Box>
                    <Box
                        ref={dropZoneRef}
                        onDrop={handleFileDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onClick={() => files.length < MAX_FILES && fileInputRef.current?.click()}
                        sx={{
                            border: '2px dashed',
                            borderColor: fieldErrors.files ? 'error.main' : dragActive ? 'secondary.main' : 'divider',
                            borderRadius: 2,
                            p: 3,
                            textAlign: 'center',
                            cursor: files.length < MAX_FILES ? 'pointer' : 'default',
                            backgroundColor: dragActive ? 'action.hover' : 'transparent',
                            transition: 'all 0.2s',
                            '&:hover': files.length < MAX_FILES
                                ? { borderColor: 'secondary.main', backgroundColor: 'action.hover' }
                                : {},
                        }}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            multiple
                            onChange={handleFileSelect}
                            accept="image/*,video/*,.csv,.xls,.xlsx"
                        />
                        <CloudUpload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            {files.length < MAX_FILES
                                ? 'Drag & drop files here, or click to browse'
                                : 'Maximum files reached'}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                            Images, videos, CSV, Excel — max 20 MB each, up to {MAX_FILES} files ({files.length}/{MAX_FILES} added)
                        </Typography>
                    </Box>
                    {fieldErrors.files && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                            {fieldErrors.files}
                        </Typography>
                    )}
                    {files.length > 0 && (
                        <Box display="flex" flexDirection="column" gap={0.5} mt={1}>
                            {files.map((f, idx) => (
                                <Box key={`${f.name}-${idx}`} display="flex" alignItems="center" gap={1}
                                    sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: 'action.hover' }}
                                >
                                    <AttachFile fontSize="small" color="secondary" />
                                    <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {f.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {(f.size / (1024 * 1024)).toFixed(1)} MB
                                    </Typography>
                                    <IconButton size="small" onClick={() => removeFile(idx)}>
                                        <CancelIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>

                <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                    <TextField
                        label="Contact Number"
                        value={form.contactNumber}
                        onChange={handleChange('contactNumber')}
                        fullWidth
                        color="secondary"
                        placeholder="+1234567890"
                        error={!!fieldErrors.contactNumber}
                        helperText={fieldErrors.contactNumber || 'Include country code'}
                    />
                    <TextField
                        label="Mobile Number"
                        value={form.mobileNumber}
                        onChange={handleChange('mobileNumber')}
                        fullWidth
                        color="secondary"
                        placeholder="+1234567890"
                        error={!!fieldErrors.mobileNumber}
                        helperText={fieldErrors.mobileNumber || 'Include country code'}
                    />
                </Box>

                <TextField
                    label="Contact Email Address *"
                    value={form.emailAddress}
                    onChange={handleChange('emailAddress')}
                    fullWidth
                    color="secondary"
                    error={!!fieldErrors.emailAddress}
                    helperText={fieldErrors.emailAddress}
                />

                {/* CC Email Addresses with tag display */}
                <Box>
                    <TextField
                        label="CC Email Addresses"
                        value={ccInput}
                        onChange={(e) => setCcInput(e.target.value)}
                        onKeyDown={handleCcKeyDown}
                        onBlur={addCcEmail}
                        fullWidth
                        color="secondary"
                        placeholder="Type an email and press Enter"
                        error={!!fieldErrors.ccEmailAddresses}
                        helperText={fieldErrors.ccEmailAddresses || 'Press Enter or comma to add'}
                    />
                    {ccEmails.length > 0 && (
                        <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                            {ccEmails.map(email => (
                                <Chip
                                    key={email}
                                    label={email}
                                    onDelete={() => removeCcEmail(email)}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleClose}
                    disabled={saving}
                    startIcon={<CancelIcon />}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="success"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
                >
                    {saving ? 'Creating...' : 'Create Ticket'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
